(() => {
  const WISHLIST_KEY = 'hayora:wishlist';
  const TOAST_DURATION_MS = 4000;
  const TOAST_ACTION_DURATION_MS = 6000;
  const TOAST_EXIT_MS = 250;
  const TOAST_MAX_VISIBLE = 3;

  const fetchJson = async (url, options = {}) => {
    const response = await fetch(url, {
      ...options,
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...options.headers },
    });
    const body = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(body.description || body.message || window.theme.strings.error);
    return body;
  };

  const emit = (name, detail) => document.dispatchEvent(new CustomEvent(name, { detail }));

  const announceAdded = (items, state) => {
    const { cartType } = window.theme;
    if (cartType === 'drawer' && document.querySelector('cart-drawer dialog')) {
      emit('cart-drawer:open');
    } else if (cartType === 'notification' && document.querySelector('cart-notification')) {
      emit('cart-notification:open', { items, cart: state });
    } else {
      toast(window.theme.strings.addedToCart, { type: 'success' });
    }
  };

  const cart = {
    get: () => fetchJson(`${window.theme.routes.cart}.js`),

    async refresh() {
      const state = await cart.get();
      emit('cart:updated', { cart: state });
      return state;
    },

    async add(items, { sections, notify = true } = {}) {
      const payload = { items: Array.isArray(items) ? items : [items] };
      if (sections) payload.sections = sections;
      let result;
      try {
        result = await fetchJson(`${window.theme.routes.cartAdd}.js`, {
          method: 'POST',
          body: JSON.stringify(payload),
        });
      } catch (error) {
        // A TypeError means the request never reached Shopify; its browser message is not localised.
        const message = error instanceof TypeError ? window.theme.strings.error : error.message || window.theme.strings.error;
        emit('cart:error', { message, items: payload.items });
        toast(message, { type: 'error' });
        throw new Error(message);
      }
      const state = await cart.refresh().catch(() => null);
      emit('cart:added', { items: result.items, cart: state, sections: result.sections });
      if (notify) announceAdded(result.items, state);
      return result;
    },

    async change(line, quantity, { sections } = {}) {
      const payload = { line, quantity };
      if (sections) payload.sections = sections;
      const state = await fetchJson(`${window.theme.routes.cartChange}.js`, {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      emit('cart:updated', { cart: state, sections: state.sections });
      return state;
    },
  };

  const readWishlist = () => {
    try {
      const parsed = JSON.parse(localStorage.getItem(WISHLIST_KEY) || '[]');
      return Array.isArray(parsed) ? parsed.filter((handle) => typeof handle === 'string') : [];
    } catch {
      return [];
    }
  };

  const wishlist = {
    all: readWishlist,
    has: (handle) => readWishlist().includes(handle),
    toggle(handle) {
      const current = readWishlist();
      const next = current.includes(handle) ? current.filter((h) => h !== handle) : [...current, handle];
      try {
        localStorage.setItem(WISHLIST_KEY, JSON.stringify(next));
      } catch {}
      emit('wishlist:updated', { handles: next });
      return next.includes(handle);
    },
  };

  const TOAST_ICONS = {
    success: '<path d="M5 12.5l4.5 4.5L19 7.5"/>',
    error: '<circle cx="12" cy="12" r="9"/><path d="M12 7.5v5.5M12 16.5v.01"/>',
    info: '<circle cx="12" cy="12" r="9"/><path d="M12 11v5.5M12 7.5v.01"/>',
  };

  const topModal = () => {
    try {
      const modals = document.querySelectorAll('dialog:modal');
      return modals[modals.length - 1] || null;
    } catch {
      return null;
    }
  };

  // A modal dialog makes everything outside it inert, so toasts raised while one is open live inside it.
  const toastRegion = () => {
    const modal = topModal();
    if (!modal) return document.querySelector('[data-toast-region]');
    let region = modal.querySelector(':scope > .toast-region');
    if (!region) {
      region = document.createElement('div');
      region.className = 'toast-region toast-region--contained';
      region.setAttribute('role', 'status');
      region.setAttribute('aria-live', 'polite');
      modal.append(region);
    }
    return region;
  };

  const dismissToast = (item) => {
    if (!item.isConnected || item.dataset.leaving) return;
    item.dataset.leaving = 'true';
    clearTimeout(item.toastTimer);
    item.classList.add('toast--leaving');
    setTimeout(() => item.remove(), TOAST_EXIT_MS);
  };

  const scheduleToast = (item, duration) => {
    clearTimeout(item.toastTimer);
    item.toastTimer = setTimeout(() => dismissToast(item), duration);
  };

  const buildToast = (text, type, action, duration) => {
    const item = document.createElement('div');
    item.className = `toast toast--${type}`;
    item.dataset.message = text;
    if (type === 'error') item.setAttribute('role', 'alert');

    const icon = document.createElement('span');
    icon.className = 'toast__icon';
    icon.setAttribute('aria-hidden', 'true');
    icon.innerHTML = `<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round">${TOAST_ICONS[type]}</svg>`;

    const body = document.createElement('p');
    body.className = 'toast__message';
    body.textContent = text;
    item.append(icon, body);

    if (action?.label && typeof action.onClick === 'function') {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'toast__action';
      button.textContent = action.label;
      button.addEventListener('click', () => {
        dismissToast(item);
        action.onClick();
      });
      item.append(button);
    }

    const resume = () => scheduleToast(item, duration);
    item.addEventListener('pointerenter', () => clearTimeout(item.toastTimer));
    item.addEventListener('pointerleave', () => item.contains(document.activeElement) || resume());
    item.addEventListener('focusin', () => clearTimeout(item.toastTimer));
    item.addEventListener('focusout', (event) => item.contains(event.relatedTarget) || item.matches(':hover') || resume());
    return item;
  };

  const toast = (message, { type, action, duration } = {}) => {
    const region = toastRegion();
    if (!region || !message) return null;
    const text = String(message);
    const variant = TOAST_ICONS[type] ? type : text === window.theme.strings.error ? 'error' : 'info';
    const ms = duration ?? (action ? TOAST_ACTION_DURATION_MS : TOAST_DURATION_MS);

    const duplicate = action
      ? null
      : Array.from(region.children).find((el) => !el.dataset.leaving && el.dataset.message === text);
    if (duplicate) {
      scheduleToast(duplicate, ms);
      return duplicate;
    }

    const item = buildToast(text, variant, action, ms);
    region.append(item);
    const visible = Array.from(region.children).filter((el) => !el.dataset.leaving);
    visible.slice(0, Math.max(0, visible.length - TOAST_MAX_VISIBLE)).forEach(dismissToast);
    scheduleToast(item, ms);
    return item;
  };

  const formatMoney = (cents) => {
    const format = window.theme.moneyFormat || '€{{amount_with_comma_separator}}';
    const value = (cents / 100).toFixed(2);
    const [whole, fraction] = value.split('.');
    const withDots = whole.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    const withCommas = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      if (key === 'amount_with_comma_separator') return `${withDots},${fraction}`;
      if (key === 'amount_no_decimals') return withCommas;
      if (key === 'amount_no_decimals_with_comma_separator') return withDots;
      return `${withCommas}.${fraction}`;
    });
  };

  const fetchSection = async (url, sectionId) => {
    const separator = url.includes('?') ? '&' : '?';
    const response = await fetch(`${url}${separator}section_id=${encodeURIComponent(sectionId)}`);
    if (!response.ok) throw new Error(window.theme.strings.error);
    return new DOMParser().parseFromString(await response.text(), 'text/html');
  };

  Object.assign(window.theme, { cart, wishlist, toast, formatMoney, fetchSection, emit });
})();
